import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SEO } from '../components/SEO';
import {
  Search, ChevronRight, ArrowRight, Utensils, Moon, Dumbbell, Compass, Gift, ShoppingCart, Wine,
  Users, TrendingUp, MapPin, Clock, Flame, Sparkles, Bell, Heart
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import DealCard from '../components/DealCard';
import MapSection from '../components/MapSection';
import SpotlightBanner from '../components/SpotlightBanner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  { name: 'Food & Drink', icon: Utensils, desc: 'Restaurants, cafes & bars', color: '#C8A97E', bg: '#FBF5EC' },
  { name: 'Nightlife', icon: Moon, desc: 'Bars, clubs & events', color: '#8A9A8B', bg: '#EEF1EE' },
  { name: 'Fitness & Wellness', icon: Dumbbell, desc: 'Gyms, yoga & wellness', color: '#7BA05B', bg: '#EDF3E8' },
  { name: 'Activities', icon: Compass, desc: 'Escape rooms & fun', color: '#A07BC8', bg: '#F1EDF9' },
  { name: 'Free Deals', icon: Gift, desc: 'Freebies & giveaways', color: '#C87B7B', bg: '#F9EDEC' },
  { name: 'Convenience Stores', icon: ShoppingCart, desc: 'Snacks, essentials & more', color: '#5B9EA0', bg: '#EBF5F5' },
  { name: 'Liquor Stores', icon: Wine, desc: 'Deals on drinks — 21+', color: '#8B4A62', bg: '#F5EBF0' },
];

function DailyPerksSignup({ variant = 'full', className = '' }) {
  const [email, setEmail] = useState('');
  const [cityId, setCityId] = useState('athens_ga');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await fetch(`${API}/email/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, city_id: cityId }),
      });
      setSubmitted(true);
    } catch {}
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className={`flex items-center justify-center gap-3 ${className}`} data-testid="email-signup-success">
        <div className="w-8 h-8 rounded-full bg-[#C8A97E]/20 flex items-center justify-center">
          <Heart size={16} className="text-[#C8A97E]" fill="#C8A97E" />
        </div>
        <p className="font-semibold text-[#2B2B2B]">You're in! Daily deals coming your way.</p>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <form onSubmit={handleSubmit} className={`flex flex-col sm:flex-row gap-2 ${className}`} data-testid="compact-email-form">
        <input
          type="email" required value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email address"
          className="perkly-input flex-1 text-sm py-3"
          data-testid="compact-email-input"
        />
        <button type="submit" disabled={loading} className="btn-primary text-sm py-3 px-6 disabled:opacity-60 whitespace-nowrap sm:w-auto" data-testid="compact-email-submit">
          {loading ? '...' : 'Subscribe Free'}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-3 ${className}`} data-testid="daily-perks-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input
          type="email" required value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email address"
          className="perkly-input text-sm py-3.5"
          data-testid="daily-perks-email-input"
        />
        <select
          value={cityId} onChange={(e) => setCityId(e.target.value)}
          className="perkly-input text-sm py-3.5"
          data-testid="daily-perks-city-select"
        >
          <option value="athens_ga">Athens, GA</option>
          <option value="atlanta_ga">Atlanta, GA (Coming Soon)</option>
          <option value="savannah_ga">Savannah, GA (Coming Soon)</option>
        </select>
        <button type="submit" disabled={loading} className="btn-primary justify-center py-3.5 text-sm disabled:opacity-60" data-testid="daily-perks-submit">
          {loading ? '...' : <><Bell size={15} /> Subscribe Free</>}
        </button>
      </div>
      <p className="text-xs text-[#2B2B2B]/45 text-center">No spam, ever · Unsubscribe anytime · 100% free</p>
    </form>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const [featuredDeals, setFeaturedDeals] = useState([]);
  const [endingSoon, setEndingSoon] = useState([]);
  const [trending, setTrending] = useState([]);
  const [allDeals, setAllDeals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [email, setEmail] = useState('');
  const [liveStats, setLiveStats] = useState({ members: 0, verified: 0, active_deals: 0, businesses: 0 });
  const [heroImage, setHeroImage] = useState({
    image_url: 'https://images.unsplash.com/photo-1557944941-6e2dae30f0aa?w=1400&q=80',
    overlay_opacity: 55,
  });
  const endingSoonRef = useRef(null);

  useEffect(() => {
    // Load hero image from admin settings
    fetch(`${API}/settings/hero`)
      .then(r => r.json())
      .then(d => setHeroImage(d))
      .catch(() => {});
    // Featured deals
    fetch(`${API}/deals?featured=true&limit=6`)
      .then((r) => r.json())
      .then((d) => setFeaturedDeals(d.deals || []))
      .catch(() => {});

    // Live stats — polls every 10s (members, deals, businesses)
    const fetchStats = () =>
      fetch(`${API}/stats/live`)
        .then((r) => r.json())
        .then((d) => setLiveStats(d))
        .catch(() => {});
    fetchStats();
    const statsInterval = setInterval(fetchStats, 10000);

    // All deals for map + trending
    fetch(`${API}/deals?limit=50&city_id=athens_ga`)
      .then((r) => r.json())
      .then((d) => {
        const deals = d.deals || [];
        setAllDeals(deals);
        const sorted = [...deals].sort((a, b) => (b.views || 0) - (a.views || 0));
        setTrending(sorted.slice(0, 8));
        const now = new Date();
        const soon = deals.filter((d) => {
          if (!d.end_date) return false;
          const end = new Date(d.end_date);
          const diffDays = (end - now) / (1000 * 60 * 60 * 24);
          return diffDays >= 0 && diffDays <= 30;
        });
        setEndingSoon(soon.slice(0, 6));
      })
      .catch(() => {});

    return () => clearInterval(statsInterval);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) navigate(`/explore?search=${encodeURIComponent(searchQuery.trim())}`);
  };

  const getDaysLeft = (endDate) => {
    if (!endDate) return null;
    const diff = Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (diff <= 0) return 'Ends today';
    if (diff === 1) return '1 day left';
    return `${diff} days left`;
  };

  return (
    <div className="min-h-screen bg-[#F6F1E9]">
      <SEO rawTitle="Perkly | Student Deals &amp; Local Experiences in Athens, GA" canonical="/" />
      <Navbar />
      <section data-testid="hero-section" className="relative min-h-[88vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage.image_url})` }} />
        <div className="absolute inset-0" style={{ backgroundColor: `rgba(43,43,43,${(heroImage.overlay_opacity || 55) / 100})` }} />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full mb-6 animate-fade-in-up">
            <MapPin size={12} className="text-[#C8A97E]" /> Athens, GA — Now Live
          </div>
          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight mb-5 animate-fade-in-up animate-delay-100"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="hero-headline"
          >
            Discover the Best <span className="text-[#C8A97E]">Deals</span>, Food & Experiences
          </h1>
          <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed animate-fade-in-up animate-delay-200" data-testid="hero-subtext">
            Find cheap eats, nightlife, fitness deals, and local events — all in one place. Built for students &amp; locals.
          </p>

          <form onSubmit={handleSearch} className="relative max-w-xl mx-auto mb-8 animate-fade-in-up animate-delay-300">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A9A8B]" />
            <input
              data-testid="hero-search-input"
              type="text" value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search deals, food, events..."
              className="w-full pl-12 pr-36 py-4 rounded-full bg-white text-[#2B2B2B] text-sm font-medium shadow-lg focus:outline-none focus:ring-2 focus:ring-[#C8A97E]/40"
            />
            <button type="submit" data-testid="hero-search-button" className="absolute right-2 top-1/2 -translate-y-1/2 btn-primary text-sm py-2 px-5">
              Search
            </button>
          </form>

          <div className="flex flex-wrap items-center justify-center gap-4 mb-10 animate-fade-in-up animate-delay-400">
            <Link to="/explore" data-testid="hero-cta-explore" className="btn-primary px-8 py-3.5 text-base">
              Explore Deals <ArrowRight size={16} />
            </Link>
            <Link to="/business/register" data-testid="hero-cta-business" className="btn-secondary px-8 py-3.5 text-base text-white border-white hover:bg-white hover:text-[#2B2B2B]">
              List Your Business
            </Link>
          </div>

          {/* Hero Daily Perks inline */}
          <div className="animate-fade-in-up animate-delay-500 max-w-lg mx-auto">
            <p className="text-white/60 text-xs mb-3 flex items-center justify-center gap-2">
              <Bell size={12} className="text-[#C8A97E]" /> Get daily deals delivered to your inbox
            </p>
            <DailyPerksSignup variant="compact" />
          </div>
        </div>
      </section>

      {/* PERKLY SPOTLIGHT — isolated section; renders nothing when disabled */}
      <SpotlightBanner />

      {/* CATEGORIES */}
      <section data-testid="categories-section" className="py-20 px-4 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A8B] mb-3">Browse by Category</p>
          <h2 className="text-4xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="categories-title">
            What Are You Looking For?
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {CATEGORIES.map((cat) => (
            <Link
              key={cat.name}
              to={`/explore?category=${encodeURIComponent(cat.name)}`}
              data-testid={`category-card-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
              className="category-card rounded-2xl p-6 text-center cursor-pointer border border-transparent hover:border-[#C8A97E]/30"
              style={{ backgroundColor: cat.bg }}
            >
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                style={{ backgroundColor: cat.color + '20' }}>
                <cat.icon size={22} style={{ color: cat.color }} />
              </div>
              <h3 className="text-sm font-bold text-[#2B2B2B] mb-1">{cat.name}</h3>
              <p className="text-xs text-[#2B2B2B]/55 leading-relaxed hidden sm:block">{cat.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED DEALS */}
      <section data-testid="featured-deals-section" className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A8B] mb-2">Hot Right Now</p>
              <h2 className="text-4xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Featured Deals
              </h2>
            </div>
            <Link to="/explore" className="hidden sm:flex items-center gap-2 text-sm font-semibold text-[#C8A97E] hover:gap-3 transition-all">
              View All <ChevronRight size={16} />
            </Link>
          </div>
          {featuredDeals.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredDeals.map((deal) => <DealCard key={deal.deal_id} deal={deal} />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-[#F6F1E9] rounded-2xl h-72 animate-pulse" />
              ))}
            </div>
          )}
          <div className="text-center mt-10">
            <Link to="/explore" data-testid="view-all-deals-button" className="btn-primary px-10 py-3.5 inline-flex">
              Explore All Deals <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* DAILY PERKS SIGNUP — Banner */}
      <section data-testid="daily-perks-section" className="py-16 px-4 bg-[#F6F1E9]">
        <div className="max-w-5xl mx-auto">
          <div className="bg-[#2B2B2B] rounded-3xl p-8 sm:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-full bg-[#C8A97E]/20 flex items-center justify-center">
                    <Bell size={16} className="text-[#C8A97E]" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C8A97E]">Daily Perks Newsletter</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Get Daily Deals <br />Near You
                </h2>
                <p className="text-white/60 text-sm leading-relaxed mb-2">
                  Subscribe to Daily Perks and receive the best food, fitness, nightlife, and event deals in your area every morning.
                </p>
                <div className="flex flex-wrap gap-3 mt-4">
                  {['🍔 Food deals', '🌙 Nightlife', '💪 Fitness', '🎁 Freebies'].map((tag) => (
                    <span key={tag} className="text-xs bg-white/10 text-white/70 px-3 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
              <div>
                <DailyPerksSignup variant="full" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ENDING SOON */}
      {endingSoon.length > 0 && (
        <section data-testid="ending-soon-section" className="py-16 px-4 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C87B7B] mb-2 flex items-center gap-2">
                  <Clock size={13} /> Ending Soon
                </p>
                <h2 className="text-3xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Don't Miss These
                </h2>
              </div>
              <Link to="/explore?period=month" className="text-sm font-semibold text-[#C8A97E] flex items-center gap-1 hover:gap-2 transition-all">
                See all <ChevronRight size={15} />
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide -mx-4 px-4">
              {endingSoon.map((deal) => {
                const daysLeft = getDaysLeft(deal.end_date);
                return (
                  <Link
                    key={deal.deal_id}
                    to={`/deals/${deal.deal_id}`}
                    data-testid={`ending-soon-card-${deal.deal_id}`}
                    className="deal-card flex-shrink-0 w-64 bg-white rounded-2xl overflow-hidden border border-[#8A9A8B]/10 shadow-sm group"
                  >
                    <div className="relative h-36 overflow-hidden">
                      <img src={deal.image_url} alt={deal.title} className="deal-card-image w-full h-full object-cover"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&q=60'; }} />
                      {daysLeft && (
                        <div className="absolute bottom-2 left-2 bg-[#C87B7B] text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                          <Clock size={10} /> {daysLeft}
                        </div>
                      )}
                    </div>
                    <div className="p-3.5">
                      <p className="text-xs text-[#8A9A8B] font-semibold mb-1">{deal.business_name}</p>
                      <h3 className="text-sm font-bold text-[#2B2B2B] line-clamp-2 leading-snug" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {deal.title}
                      </h3>
                      {deal.discount_text && <p className="text-xs text-[#C8A97E] font-bold mt-1">{deal.discount_text}</p>}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* TRENDING THIS WEEK */}
      {trending.length > 0 && (
        <section data-testid="trending-section" className="py-16 px-4 bg-[#F6F1E9]">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A8B] mb-2 flex items-center gap-2">
                  <Flame size={13} className="text-[#C8A97E]" /> Student Favorites
                </p>
                <h2 className="text-3xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Trending This Week
                </h2>
              </div>
              <Link to="/explore" className="text-sm font-semibold text-[#C8A97E] flex items-center gap-1 hover:gap-2 transition-all">
                See all <ChevronRight size={15} />
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide -mx-4 px-4">
              {trending.map((deal, i) => (
                <Link
                  key={deal.deal_id}
                  to={`/deals/${deal.deal_id}`}
                  data-testid={`trending-card-${deal.deal_id}`}
                  className="deal-card flex-shrink-0 w-56 bg-white rounded-2xl overflow-hidden border border-[#8A9A8B]/10 shadow-sm group"
                >
                  <div className="relative h-32 overflow-hidden">
                    <img src={deal.image_url} alt={deal.title} className="deal-card-image w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&q=60'; }} />
                    <div className="absolute top-2 left-2 bg-[#2B2B2B] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      #{i + 1}
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-[#8A9A8B] font-semibold mb-1">{deal.business_name}</p>
                    <h3 className="text-sm font-bold text-[#2B2B2B] line-clamp-2 leading-snug" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {deal.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MAP SECTION */}
      <section data-testid="map-section-wrapper" className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <MapSection deals={allDeals} />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="how-it-works-section" className="py-20 px-4 max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A8B] mb-3">Simple as 1-2-3</p>
          <h2 className="text-4xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>
            How Perkly Works
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="bg-white rounded-3xl p-8 border border-[#8A9A8B]/10">
            <div className="flex items-center gap-3 mb-7">
              <div className="w-10 h-10 rounded-full bg-[#C8A97E]/15 flex items-center justify-center">
                <Users size={20} className="text-[#C8A97E]" />
              </div>
              <h3 className="text-xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>For Students & Locals</h3>
            </div>
            <div className="space-y-5">
              {[
                { num: '01', title: 'Browse deals near you', desc: 'Search by category, city, or time period. Use the map to discover deals around you.' },
                { num: '02', title: 'Tap to claim a deal', desc: "Tap 'Claim This Deal' to open a 15-minute window. Ask the staff for today's 4-digit PIN." },
                { num: '03', title: 'Enter the PIN, save money', desc: 'Type the 4 digits the staff gives you — your deal is locked in instantly. Come back tomorrow for a fresh PIN.' },
              ].map((step) => (
                <div key={step.num} className="flex gap-4">
                  <span className="text-3xl font-bold text-[#C8A97E]/30 leading-none">{step.num}</span>
                  <div>
                    <h4 className="font-bold text-[#2B2B2B] mb-0.5">{step.title}</h4>
                    <p className="text-sm text-[#2B2B2B]/55 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/explore" className="btn-primary mt-8 inline-flex text-sm" data-testid="how-it-works-explore-btn">
              Start Exploring <ArrowRight size={14} />
            </Link>
          </div>
          <div className="bg-[#2B2B2B] rounded-3xl p-8">
            <div className="flex items-center gap-3 mb-7">
              <div className="w-10 h-10 rounded-full bg-[#C8A97E]/20 flex items-center justify-center">
                <TrendingUp size={20} className="text-[#C8A97E]" />
              </div>
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>For Local Businesses</h3>
            </div>
            <div className="space-y-5">
              {[
                { num: '01', title: 'List your deal', desc: 'Submit your deal in minutes. A rotating daily PIN lets you track every customer Perkly sends you.' },
                { num: '02', title: 'Reach your audience', desc: 'Connect with thousands of students and locals actively looking for deals in your city.' },
                { num: '03', title: 'Grow foot traffic', desc: "Watch more customers walk through your door — and see every redemption in your dashboard in real time." },
              ].map((step) => (
                <div key={step.num} className="flex gap-4">
                  <span className="text-3xl font-bold text-[#C8A97E]/40 leading-none">{step.num}</span>
                  <div>
                    <h4 className="font-bold text-white mb-0.5">{step.title}</h4>
                    <p className="text-sm text-white/55 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-8 flex-wrap">
              <Link to="/business/register" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C8A97E] text-white text-sm font-bold hover:bg-[#b8956b] transition-colors" data-testid="how-it-works-business-btn">
                List Your Deal <ArrowRight size={14} />
              </Link>
              <Link to="/pricing" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/30 text-white text-sm font-semibold hover:bg-white/10 transition-colors">
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FOR BUSINESSES */}
      <section data-testid="for-businesses-section" className="py-20 px-4 bg-[#F6F1E9]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A8B] mb-4">Grow with Perkly</p>
              <h2 className="text-4xl lg:text-5xl font-bold text-[#2B2B2B] leading-tight mb-5" style={{ fontFamily: 'Playfair Display, serif' }}>
                Grow Your Business with <span className="text-[#C8A97E]">Perkly</span>
              </h2>
              <p className="text-base text-[#2B2B2B]/65 leading-relaxed mb-6">
                Promote your deals, track every redemption with daily PINs, and reach thousands of students daily.
              </p>
              {/* Free month badge */}
              <div className="inline-flex items-center gap-2 bg-[#C8A97E]/12 border border-[#C8A97E]/30 rounded-2xl px-5 py-3 mb-6">
                <Sparkles size={16} className="text-[#C8A97E]" />
                <p className="text-sm font-bold text-[#2B2B2B]">First month free · No credit card needed</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
                {[
                  { label: 'Easy Setup', desc: 'Live in minutes' },
                  { label: 'PIN Tracking', desc: 'See every redemption' },
                  { label: 'Map Listing', desc: 'Students find you' },
                ].map((f) => (
                  <div key={f.label} className="bg-white rounded-2xl p-4 border border-[#8A9A8B]/10">
                    <h4 className="font-bold text-[#2B2B2B] text-sm mb-1">{f.label}</h4>
                    <p className="text-xs text-[#2B2B2B]/55">{f.desc}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 flex-wrap">
                <Link to="/business/register" data-testid="for-businesses-cta" className="btn-primary inline-flex text-base px-8 py-3.5">
                  List Your Deal <ArrowRight size={16} />
                </Link>
                <Link to="/pricing" className="btn-secondary inline-flex text-base px-8 py-3.5">
                  View Pricing
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1594404430367-9858af713541?w=700&q=80"
                alt="Business owner"
                className="w-full rounded-3xl object-cover h-[420px] shadow-xl"
              />
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-lg border border-[#8A9A8B]/10">
                <p className="text-xs text-[#8A9A8B] font-semibold mb-1">This week</p>
                <p className="text-2xl font-bold text-[#2B2B2B]">+340</p>
                <p className="text-xs text-[#2B2B2B]/60">new deal views</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="py-16 px-4 bg-white" data-testid="social-proof-section">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A8B] mb-3">Community</p>
          <h2 className="text-3xl font-bold text-[#2B2B2B] mb-10" style={{ fontFamily: 'Playfair Display, serif' }}>
            Join Thousands Discovering Local Deals
          </h2>
          <div className="grid grid-cols-3 gap-6 max-w-sm mx-auto">
            {[
              {
                num: liveStats.members > 0 ? liveStats.members.toLocaleString() : '0',
                label: 'Members',
                testid: 'stat-members',
              },
              {
                num: liveStats.active_deals > 0 ? liveStats.active_deals.toLocaleString() : '0',
                label: 'Active Deals',
                testid: 'stat-deals',
              },
              {
                num: liveStats.businesses > 0 ? liveStats.businesses.toLocaleString() : '0',
                label: 'Businesses',
                testid: 'stat-businesses',
              },
            ].map((stat) => (
              <div key={stat.label} className="text-center" data-testid={stat.testid}>
                <p
                  className="text-3xl font-bold text-[#C8A97E] tabular-nums transition-all duration-500"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {stat.num}
                </p>
                <p className="text-xs text-[#2B2B2B]/60 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
          {liveStats.verified > 0 && (
            <p className="text-sm text-[#2B2B2B]/50 mt-6 flex items-center justify-center gap-2" data-testid="live-joined-banner">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              🔥 {liveStats.verified.toLocaleString()} people joined Perkly in Athens
            </p>
          )}
        </div>
      </section>

      {/* EMAIL SIGNUP — Footer version */}
      <section data-testid="email-signup-section" className="py-20 px-4 bg-[#2B2B2B]">
        <div className="max-w-xl mx-auto text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C8A97E] mb-4">Daily Perks</p>
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Never Miss a Deal
          </h2>
          <p className="text-white/60 mb-8 text-sm">Get the best deals and events near you, delivered every morning.</p>
          <DailyPerksSignup variant="compact" />
        </div>
      </section>

      <Footer />
    </div>
  );
}
